import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, FileSpreadsheet, FileText, Archive, MoreVertical, Eye, Copy, Phone } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AdminTitle } from "@/admin/AdminLayout";
import { APP_STATUSES, CARD, Badge, Empty, Listing, copyText, download, expirySoon, fmtDay, useList } from "@/admin/hr/common";
import ApplicationDetail from "@/admin/hr/ApplicationDetail";
import { ReportFieldsButton, ReportFieldsDialog, useReportFields } from "@/admin/hr/ReportFields";

const INIT = { q: "", status: "", card_type: "", date_from: "", date_to: "", docs: "", expiring: "", job_id: "" };

function inputValue(t) {
  if (t.type !== "checkbox") return t.value;
  return t.checked ? "1" : "";
}

function Filters({ f, setF }) {
  const [jobs] = useList("/admin/hr/jobs", {});
  const set = (k) => (e) => setF({ ...f, [k]: inputValue(e.target) });
  return (
    <div className="mb-4 grid gap-3 rounded-lg border border-zinc-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="applications-filters">
      <div className="relative sm:col-span-2">
        <Search className="absolute left-3 top-3.5 h-4 w-4 text-zinc-400" />
        <input value={f.q} onChange={set("q")} placeholder="Ad soyad, telefon veya başvuru no..." className="field-input !pl-9" data-testid="filter-q" />
      </div>
      <select value={f.status} onChange={set("status")} className="field-input" data-testid="filter-status">
        <option value="">Tüm durumlar</option>{APP_STATUSES.map((s) => <option key={s}>{s}</option>)}
      </select>
      <select value={f.job_id} onChange={set("job_id")} className="field-input sm:col-span-2" data-testid="filter-job">
        <option value="">Tüm görev ve ilanlar</option>{(jobs || []).map((j) => <option key={j.id} value={j.id}>{j.title} ({j.applications}){j.active ? "" : " – pasif"}</option>)}
      </select>
      <select value={f.card_type} onChange={set("card_type")} className="field-input" data-testid="filter-card">
        <option value="">Silahlı + silahsız (tümü)</option><option value="silahli">Silahlı</option><option value="silahsiz">Silahsız</option>
      </select>
      <label className="text-xs font-semibold text-zinc-600">Başlangıç<input type="date" value={f.date_from} onChange={set("date_from")} className="field-input" data-testid="filter-date-from" /></label>
      <label className="text-xs font-semibold text-zinc-600">Bitiş<input type="date" value={f.date_to} onChange={set("date_to")} className="field-input" data-testid="filter-date-to" /></label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.docs === "missing"} onChange={(e) => setF({ ...f, docs: e.target.checked ? "missing" : "" })} className="h-4 w-4 accent-[color:var(--red)]" data-testid="filter-missing-docs" /> Belgesi eksik olanlar</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.expiring} onChange={set("expiring")} className="h-4 w-4 accent-[color:var(--red)]" data-testid="filter-expiring" /> Kart süresi 60 gün içinde dolanlar</label>
    </div>
  );
}

function RowMenu({ r, open }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded p-1.5 hover:bg-zinc-100" onClick={(e) => e.stopPropagation()} data-testid={`row-menu-${r.id}`}><MoreVertical className="h-4 w-4" /></DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => open(r)}><Eye className="mr-2 h-4 w-4" /> Görüntüle</DropdownMenuItem>
        <DropdownMenuItem onClick={() => copyText(r.phone)}><Copy className="mr-2 h-4 w-4" /> Telefonu kopyala</DropdownMenuItem>
        <DropdownMenuItem asChild><a href={`tel:${r.phone}`}><Phone className="mr-2 h-4 w-4" /> Ara</a></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Table({ rows, sel, setSel, open }) {
  const all = rows.length > 0 && rows.every((r) => sel.includes(r.id));
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table className="w-full text-sm" data-testid="applications-table">
        <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="px-3 py-3"><input type="checkbox" checked={all} onChange={() => setSel(all ? [] : rows.map((r) => r.id))} aria-label="Tümünü seç" data-testid="select-all" /></th>
            {["Başvuru No", "Tarih", "Ad Soyad", "T.C. No", "Telefon", "Görev / İlan", "İl / İlçe", "Kart", "Kart Bitiş", "Belge", "Durum", ""].map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <tr key={r.id} onClick={() => open(r)} className="cursor-pointer hover:bg-zinc-50" data-testid={`application-row-${r.id}`}>
              <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`select-${r.id}`} /></td>
              <td className="whitespace-nowrap px-3 py-3 font-semibold text-[color:var(--red)]">{r.app_no}</td>
              <td className="whitespace-nowrap px-3 py-3 text-zinc-500">{fmtDate(r.created_at)}</td>
              <td className="px-3 py-3 font-semibold text-zinc-900">{r.name}</td>
              <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-zinc-600">{r.tc_masked || "-"}</td>
              <td className="whitespace-nowrap px-3 py-3">{r.phone}</td>
              <td className="min-w-[160px] px-3 py-3 text-zinc-700" data-testid={`row-job-${r.id}`}>{r.job?.title || r.position || "-"}</td>
              <td className="px-3 py-3 text-zinc-600">{[r.city, r.district].filter(Boolean).join(" / ") || "-"}</td>
              <td className="whitespace-nowrap px-3 py-3">{CARD[r.card_type] || "-"}</td>
              <td className={`whitespace-nowrap px-3 py-3 ${expirySoon(r.card_expiry) ? "font-semibold text-orange-700" : ""}`}>{fmtDay(r.card_expiry)}</td>
              <td className="px-3 py-3"><Badge value={r.doc_status} /></td>
              <td className="px-3 py-3"><Badge value={r.status} /></td>
              <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><RowMenu r={r} open={open} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Applications() {
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState({ ...INIT, job_id: params.get("job_id") || "" });
  const [rows, load] = useList("/admin/hr/applications", f);
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(null);
  const [pick, setPick] = useState(false);
  const rf = useReportFields();
  useEffect(() => {
    const id = params.get("id");
    if (id) api.get(`/admin/hr/applications/${id}`).then((r) => setOpen(r.data)).catch(() => toast.error("Başvuru bulunamadı"));
  }, [params]);
  const close = () => { setOpen(null); if (params.get("id")) setParams({}); };
  const exportFile = (type) => {
    if (!rf.picked.length) return toast.error("Rapor için en az bir alan seçin.");
    const params = { ...f, ids: sel.join(","), fields: rf.picked.join(",") };
    return type === "xlsx" ? download("/admin/hr/export/applications.xlsx", "basvurular.xlsx", { params })
      : download("/admin/hr/export/applications.pdf", "basvuru-raporu.pdf", { params });
  };
  const zip = () => (sel.length ? download("/admin/hr/export/documents.zip", "basvuru-belgeleri.zip", { method: "post", data: { ids: sel.slice(0, 50) } }) : toast.error("Önce başvuru seçin"));
  return (
    <div>
      <AdminTitle title="İş Başvuruları" desc={rows ? `${rows.length} başvuru${sel.length ? ` · ${sel.length} seçili` : ""}` : ""}>
        <ReportFieldsButton onClick={() => setPick(true)} rf={rf} />
        <button onClick={() => exportFile("xlsx")} className="btn-outline !py-2" data-testid="apps-export-xlsx"><FileSpreadsheet className="h-4 w-4" /> Excel</button>
        <button onClick={() => exportFile("pdf")} className="btn-outline !py-2" data-testid="apps-export-pdf"><FileText className="h-4 w-4" /> PDF</button>
        <button onClick={zip} className="btn-red !py-2" data-testid="apps-export-zip"><Archive className="h-4 w-4" /> Seçilenlerin Belgeleri (ZIP)</button>
      </AdminTitle>
      <ReportFieldsDialog open={pick} onOpenChange={setPick} rf={rf} onExport={exportFile} />
      <Filters f={f} setF={setF} />
      <Listing rows={rows} empty={<Empty testid="applications-empty" />}><Table rows={rows || []} sel={sel} setSel={setSel} open={setOpen} /></Listing>
      <Dialog open={!!open} onOpenChange={(o) => !o && close()}>
        {open && <ApplicationDetail key={open.id} item={open} onChanged={load} onClose={close} />}
      </Dialog>
    </div>
  );
}
